import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Fab,
  Paper,
  Typography,
  IconButton,
  TextField,
  List,
  ListItem,
  Avatar,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Chat,
  Close,
  Send,
  SmartToy,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { getLanguage } from '@/utils/i18n';
import EmbedAPIClient from '@embedapi/core';

// Initialize EmbedAPI client directly with the key (for MVP)
const embedClient = new EmbedAPIClient('01xJgJ_bGEWTQjG6O2xsEuN6TqGk9LN0');

interface Message {
  id: string;
  text: string;
  sender: 'ai' | 'user';
  timestamp: Date;
}

const AIChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showPitch, setShowPitch] = useState(false);
  
  useEffect(() => {
    // Show pitch bubble 3 seconds after load
    const timer = setTimeout(() => {
      if (!isOpen) setShowPitch(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, [isOpen]);

  const handleOpen = () => {
    setIsOpen(!isOpen);
    setShowPitch(false); // Hide pitch if they open it
  }
  
  const getInitialMessage = () => {
    const lang = getLanguage();
    if (lang === 'fr') return "Bonjour! Je suis votre conseiller IA souk.immo. Cherchez-vous à investir ou à trouver une maison pour votre famille?";
    if (lang === 'ar') return "سلام! أنا مستشار souk.immo للذكاء الاصطناعي. واش كتقلب على استثمار ولا دار للعائلة؟";
    return "Salam! I'm your souk.immo AI Advisor. Are you looking to invest or find a home for your family?";
  };

  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: getInitialMessage(),
      sender: 'ai',
      timestamp: new Date(),
    },
  ]);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const userMsg: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date(),
    };

    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInputValue('');
    setIsTyping(true);

    try {
      const apiMessages = updatedMessages.map(msg => ({
        role: msg.sender === 'ai' ? 'assistant' : 'user',
        content: msg.text
      }));

      const lang = getLanguage();
      const systemPrompt = `You are a helpful real estate assistant on souk.immo platform. Respond in this language code: ${lang}. Be concise, friendly, and helpful.`;

      const result = await embedClient.generate({
        service: 'openai',
        model: 'gpt-4o-mini',
        messages: [{ role: 'system', content: systemPrompt }, ...apiMessages],
      });
      
      const aiResponseText = result?.choices?.[0]?.message?.content || result?.answer || "I apologize, but I am unable to respond at this moment.";

      const aiMsg: Message = {
        id: (Date.now() + 1).toString(),
        text: aiResponseText,
        sender: 'ai',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, aiMsg]);
    } catch (error) {
      console.error('Error calling EmbedAPI:', error);
      const errorMsg: Message = {
        id: (Date.now() + 1).toString(),
        text: "Sorry, I am currently experiencing technical difficulties. Please try again later.",
        sender: 'ai',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <Box sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 2000 }}>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
          >
            <Paper
              elevation={6}
              sx={{
                width: { xs: 'calc(100vw - 64px)', sm: 380 },
                height: 500,
                mb: 2,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                borderRadius: 3,
                border: '1px solid rgba(197, 160, 89, 0.2)',
              }}
            >
              {/* Header */}
              <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'white' }}>
                    <SmartToy sx={{ color: 'primary.main' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>souk.immo AI</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>Online • Advisor</Typography>
                  </Box>
                </Box>
                <IconButton size="small" onClick={() => setIsOpen(false)} sx={{ color: 'white' }}>
                  <Close fontSize="small" />
                </IconButton>
              </Box>

              {/* Messages */}
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: '#F0EDE9' }}>
                <List disablePadding>
                  {messages.map((msg) => (
                    <ListItem
                      key={msg.id}
                      sx={{
                        flexDirection: 'column',
                        alignItems: msg.sender === 'ai' ? 'flex-start' : 'flex-end',
                        mb: 2,
                        px: 0,
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: '85%',
                          p: 2,
                          borderRadius: 2,
                          bgcolor: msg.sender === 'ai' ? 'white' : 'primary.main',
                          color: msg.sender === 'ai' ? 'text.primary' : 'white',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        }}
                      >
                        <Typography variant="body2">{msg.text}</Typography>
                      </Box>
                      <Typography variant="caption" sx={{ mt: 0.5, opacity: 0.6, fontSize: '0.65rem' }}>
                        {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </ListItem>
                  ))}
                  
                  {isTyping && (
                    <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', px: 0 }}>
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'white', display: 'flex', gap: 0.5 }}>
                        <motion.div animate={{ y: [0, -5, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0 }}><Box sx={{ width: 6, height: 6, bgcolor: 'primary.main', borderRadius: '50%' }} /></motion.div>
                        <motion.div animate={{ y: [0, -5, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.2 }}><Box sx={{ width: 6, height: 6, bgcolor: 'primary.main', borderRadius: '50%' }} /></motion.div>
                        <motion.div animate={{ y: [0, -5, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.4 }}><Box sx={{ width: 6, height: 6, bgcolor: 'primary.main', borderRadius: '50%' }} /></motion.div>
                      </Box>
                    </ListItem>
                  )}
                  <div ref={messagesEndRef} />
                </List>
              </Box>

              <Divider />

              {/* Input */}
              <Box sx={{ p: 2, bgcolor: 'white' }}>
                <TextField
                  fullWidth
                  placeholder="Ask about properties, financing..."
                  size="small"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                  disabled={isTyping}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton color="primary" onClick={handleSend} disabled={!inputValue.trim() || isTyping}>
                          <Send />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2 }
                  }}
                />
              </Box>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPitch && !isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20 }}
            style={{
              position: 'absolute',
              bottom: 70,
              right: 0,
              width: 280,
              zIndex: 1999,
            }}
          >
            <Paper
              elevation={4}
              sx={{
                p: 2,
                borderRadius: 4,
                borderBottomRightRadius: 4,
                bgcolor: 'white',
                border: '1px solid',
                borderColor: 'primary.light',
                position: 'relative',
                cursor: 'pointer',
              }}
              onClick={handleOpen}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                {getLanguage() === 'fr' ? 'Besoin d\'aide ?' : getLanguage() === 'ar' ? 'محتاج مساعدة؟' : 'Need help?'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>
                {getLanguage() === 'fr' 
                  ? "Discutez avec notre IA pour trouver la propriété parfaite."
                  : getLanguage() === 'ar'
                  ? "تحدث مع الذكاء الاصطناعي ديالنا باش تلقى العقار المناسب."
                  : "Chat with our AI to find your perfect property."}
              </Typography>
              
              {/* Little triangle pointing to FAB */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -8,
                  right: 20,
                  width: 16,
                  height: 16,
                  bgcolor: 'white',
                  transform: 'rotate(45deg)',
                  borderBottom: '1px solid',
                  borderRight: '1px solid',
                  borderColor: 'primary.light',
                }}
              />
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>

      <Fab
        color="primary"
        aria-label="chat"
        onClick={handleOpen}
        sx={{
          boxShadow: '0 8px 32px rgba(197, 160, 89, 0.3)',
          transition: 'all 0.3s',
          transform: isOpen ? 'rotate(90deg)' : 'none',
        }}
      >
        {isOpen ? <Close /> : <Chat />}
      </Fab>
    </Box>
  );
};

export default AIChatWidget;